"use client";

import { type ReactNode, useRef, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

type MotionRevealProps = {
  children: ReactNode;
  className?: string;
  delay?: number;
  /**
   * aboveFold: LCP content never animates. Content is fully visible in SSR
   * HTML and stays visible after hydration. Eliminates the 2967ms element-
   * render-delay caused by reveal-up (animation-fill-mode:both, opacity:0)
   * being applied via rAF post-hydration, which blocked the LCP paint.
   */
  aboveFold?: boolean;
};

const DELAY_CLASS: Record<number, string> = {
  0: "reveal-up",
  0.08: "reveal-up-delayed-1",
  0.1: "reveal-up-delayed-1",
  0.16: "reveal-up-delayed-2",
  0.2: "reveal-up-delayed-2",
  0.24: "reveal-up-delayed-3",
  0.3: "reveal-up-delayed-3",
};

export function MotionReveal({ children, className, delay = 0, aboveFold = false }: MotionRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const animClass = DELAY_CLASS[delay] ?? "reveal-up";

  useEffect(() => {
    // aboveFold: never animate, content must be visible for LCP measurement.
    if (aboveFold) return;

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [aboveFold]);

  // aboveFold: always render without animation class (visible in SSR + client).
  if (aboveFold) {
    return (
      <div ref={ref} className={cn(className)}>
        {children}
      </div>
    );
  }

  return (
    <div ref={ref} className={cn(isVisible ? animClass : "opacity-0", className)}>
      {children}
    </div>
  );
}
